import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { formatRange } from "@/lib/format";
import { getActiveProject, getWeeklySummary } from "@/lib/journey.functions";
import { hasMissionWorkspaceContent } from "@/lib/mission-availability";
import {
  Calculator,
  ClipboardCheck,
  Compass,
  BookOpen,
  Heart,
  Sparkles,
  ArrowRight,
  FolderOpen,
  CalendarDays,
  BarChart3,
  Trophy,
  Lock,
  Flame,
  UserSearch,
  Target,
  CheckCircle2,
  Circle,
  Rocket,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/")({
  head: () => ({
    meta: [
      { title: "Painel — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Dashboard,
});

const TOTAL_BUSINESSES = 101;

function Dashboard() {
  const { user } = useSession();
  const firstName =
    (user?.user_metadata?.full_name as string | undefined)?.split(" ")[0] ??
    user?.email?.split("@")[0] ??
    "por aí";

  const memberSince = user?.created_at ? new Date(user.created_at) : null;
  const daysAsMember = memberSince
    ? Math.max(1, Math.ceil((Date.now() - memberSince.getTime()) / 86_400_000))
    : 1;

  const fetchActiveProject = useServerFn(getActiveProject);
  const fetchWeekly = useServerFn(getWeeklySummary);
  const { data: activeProject } = useQuery({
    queryKey: ["active-project"],
    queryFn: () => fetchActiveProject(),
    enabled: !!user,
  });
  const { data: weekly } = useQuery({
    queryKey: ["weekly-summary"],
    queryFn: () => fetchWeekly(),
    enabled: !!user,
  });

  const { data: lastDiag } = useQuery({
    queryKey: ["last-diagnostic", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("diagnostic_results")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const { data: favorites, isLoading: favLoading } = useQuery({
    queryKey: ["favorites-preview", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("favorites")
        .select("business:businesses(id, slug, title, summary, category:categories(name))")
        .limit(6);
      return data ?? [];
    },
  });

  const { data: progressRows } = useQuery({
    queryKey: ["progress-rows", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_progress")
        .select("business_id, analyzed, updated_at, business:businesses(id, slug, title, summary, category:categories(name))")
        .order("updated_at", { ascending: false });
      return data ?? [];
    },
  });

  const analyzedCount = useMemo(
    () => (progressRows ?? []).filter((p: any) => p.analyzed).length,
    [progressRows],
  );

  const lastVisited = useMemo(
    () => (progressRows ?? []).find((p: any) => p.business) as any,
    [progressRows],
  );

  const categoriesVisited = useMemo(() => {
    const set = new Set<string>();
    (progressRows ?? []).forEach((p: any) => {
      const name = p.business?.category?.name;
      if (name) set.add(name);
    });
    return set.size;
  }, [progressRows]);

  const { data: recommendations } = useQuery({
    queryKey: ["dashboard-recommendation"],
    queryFn: async () => {
      const { data } = await supabase
        .from("businesses")
        .select("id, slug, title, summary, difficulty, scalability, investment_min_cents, investment_max_cents, opportunity_score, category:categories(name)")
        .eq("active", true)
        .eq("publication_status", "published")
        .order("opportunity_score", { ascending: false, nullsFirst: false })
        .limit(1);
      return data ?? [];
    },
  });
  const recommended = recommendations?.[0] as any;

  const { data: featured } = useQuery({
    queryKey: ["dashboard-featured"],
    queryFn: async () => {
      const { data } = await supabase
        .from("businesses")
        .select("id, slug, title, summary, difficulty, investment_min_cents, investment_max_cents, category:categories(name)")
        .eq("active", true)
        .eq("publication_status", "published")
        .order("opportunity_score", { ascending: false, nullsFirst: false })
        .range(1, 6);
      return data ?? [];
    },
  });

  const progressPct = Math.min(100, Math.round((analyzedCount / TOTAL_BUSINESSES) * 100));
  const favCount = favorites?.length ?? 0;

  const journey = [
    { key: "signup", label: "Cadastro concluído", done: true },
    { key: "diag", label: "Diagnóstico de perfil", done: !!lastDiag },
    { key: "fav1", label: "Primeiro favorito", done: favCount >= 1 },
    { key: "explore10", label: "Explorar 10 negócios", done: analyzedCount >= 10 },
    { key: "plan7", label: "Completar Plano de 7 dias", done: false },
    { key: "plan30", label: "Completar Plano de 30 dias", done: false },
    { key: "clients", label: "Conseguir primeiros clientes", done: false },
    { key: "meta10k", label: "Bater R$ 10.000/mês", done: false },
  ];

  const badges = [
    { key: "fav1", label: "Primeiro Favorito", emoji: "🏅", unlocked: favCount >= 1 },
    { key: "explorer", label: "Explorador", emoji: "🧭", unlocked: analyzedCount >= 5 },
    { key: "visionary", label: "Visionário", emoji: "🔮", unlocked: !!lastDiag && analyzedCount >= 10 },
    { key: "plan", label: "Primeiro Plano", emoji: "📋", unlocked: false },
    { key: "clients", label: "Primeiros Clientes", emoji: "🤝", unlocked: false },
    { key: "meta10k", label: "Meta 10K", emoji: "💎", unlocked: false },
  ];

  const nextGoalGap = Math.max(0, 3 - (analyzedCount % 3 === 0 ? 3 : 3 - (analyzedCount % 3)));

  return (
    <div className="space-y-10">
      {/* HERO */}
      <section className="animate-fade-in relative overflow-hidden rounded-3xl border border-black/5 bg-white shadow-[0_1px_2px_rgba(12,27,51,0.04),0_20px_40px_-24px_rgba(12,27,51,0.15)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[var(--navy)]/[0.03] via-transparent to-[var(--green)]/[0.06]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[var(--green)]/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-[var(--gold)]/10 blur-3xl"
        />

        <div className="relative grid gap-8 p-6 lg:grid-cols-[1.4fr_1fr] lg:p-10">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--green)]/20 bg-[var(--green)]/[0.06] px-3 py-1 text-xs font-semibold text-[var(--green)]">
              <Sparkles size={12} /> Plataforma 101
            </span>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight lg:text-4xl">
              Bem-vindo de volta,{" "}
              <span className="capitalize">{firstName}</span>{" "}
              <span className="inline-block animate-[pulse_3s_ease-in-out_infinite]">👋</span>
            </h1>
            <p className="mt-3 max-w-xl text-[15px] text-[var(--gray-ink)]">
              Você já explorou <strong className="text-[var(--navy)]">{analyzedCount}</strong> dos{" "}
              <strong className="text-[var(--navy)]">{TOTAL_BUSINESSES}</strong> negócios disponíveis.
              Continue sua jornada e encontre a oportunidade ideal para você.
            </p>

            {/* Progress bar */}
            <div className="mt-6 max-w-xl">
              <div className="mb-2 flex items-center justify-between text-xs font-semibold text-[var(--gray-ink)]">
                <span>Seu progresso na biblioteca</span>
                <span className="text-[var(--navy)]">{progressPct}%</span>
              </div>
              <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[var(--green)] to-[var(--green)]/70 transition-[width] duration-700 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/membros/negocios"
                className="group inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 transition hover:-translate-y-0.5 hover:bg-[var(--navy)]/90"
              >
                Continuar explorando
                <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/membros/diagnostico"
                className="inline-flex items-center gap-2 rounded-xl border border-[var(--navy)]/15 bg-white px-5 py-3 text-sm font-semibold text-[var(--navy)] transition hover:-translate-y-0.5 hover:border-[var(--navy)]/30 hover:bg-[var(--gray-soft)]"
              >
                <Compass size={16} /> Refazer diagnóstico
              </Link>
            </div>
          </div>

          {/* Composição gráfica lado direito */}
          <div className="relative hidden items-center justify-center lg:flex">
            <HeroArtwork analyzed={analyzedCount} favorites={favCount} />
          </div>
        </div>
      </section>

      {/* MEU PROJETO */}
      <MyProjectBlock activeProject={activeProject} />

      {/* RESUMO DA SEMANA */}
      {weekly?.hasProject && <WeeklySummaryBlock weekly={weekly} />}




      {/* MÉTRICAS */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCard icon={<BookOpen size={18} />} value={TOTAL_BUSINESSES} label="Negócios disponíveis" accent="navy" />
        <MetricCard icon={<Heart size={18} />} value={favCount} label="Favoritos" accent="green" />
        <MetricCard icon={<BarChart3 size={18} />} value={analyzedCount} label="Explorados" accent="green" />
        <MetricCard icon={<FolderOpen size={18} />} value={categoriesVisited} label="Categorias" accent="navy" />
        <MetricCard icon={<CalendarDays size={18} />} value={daysAsMember} label="Dias como membro" accent="gold" />
        <MetricCard
          icon={<Flame size={18} />}
          value={lastVisited?.updated_at ? formatRelativeShort(lastVisited.updated_at) : "—"}
          label="Último acesso"
          accent="gold"
          isString
        />
      </section>

      {/* RECOMENDADO + CONTINUE */}
      <section className="grid gap-6 lg:grid-cols-2">
        <RecommendedCard recommended={recommended} />
        {lastVisited?.business && <ContinueCard row={lastVisited} />}
      </section>

      {/* EXPLORAR NEGÓCIOS */}
      <section>
        <SectionHeader
          eyebrow="Biblioteca"
          title="Explore novos negócios"
          subtitle="Uma amostra da biblioteca com base em oportunidade."
          action={{ to: "/membros/negocios", label: `Ver todos os ${TOTAL_BUSINESSES} negócios` }}
        />
        {featured && featured.length > 0 ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((b: any) => (
              <ExploreCard key={b.id} b={b} />
            ))}
          </div>
        ) : (
          <SkeletonGrid n={6} />
        )}
      </section>

      {/* JORNADA + CONQUISTAS */}
      <section className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="rounded-3xl border border-black/5 bg-white p-6 lg:p-8">
          <SectionTitle icon={<Rocket size={16} />} title="Sua jornada" />
          <ol className="mt-5 space-y-3">
            {journey.map((step, idx) => (
              <li
                key={step.key}
                className={`group flex items-start gap-3 rounded-xl border p-3 transition ${
                  step.done
                    ? "border-[var(--green)]/20 bg-[var(--green)]/[0.04]"
                    : "border-black/5 bg-[var(--gray-soft)]/40"
                }`}
              >
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition ${
                    step.done
                      ? "bg-[var(--green)] text-white animate-scale-in"
                      : "border border-black/10 bg-white text-[var(--gray-ink)]"
                  }`}
                >
                  {step.done ? <CheckCircle2 size={14} /> : <Circle size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-semibold ${
                      step.done ? "text-[var(--navy)]" : "text-[var(--gray-ink)]"
                    }`}
                  >
                    {String(idx + 1).padStart(2, "0")}. {step.label}
                  </p>
                </div>
                {step.done && (
                  <span className="rounded-full bg-[var(--green)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--green)]">
                    Concluído
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-3xl border border-black/5 bg-white p-6 lg:p-8">
          <SectionTitle icon={<Trophy size={16} />} title="Conquistas" />
          <p className="mt-2 text-sm text-[var(--gray-ink)]">
            Colecione medalhas ao avançar na plataforma.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {badges.map((badge) => (
              <div
                key={badge.key}
                className={`group flex flex-col items-center gap-1.5 rounded-2xl border p-3 text-center transition hover:-translate-y-0.5 ${
                  badge.unlocked
                    ? "border-[var(--gold)]/30 bg-gradient-to-b from-[var(--gold)]/[0.08] to-transparent"
                    : "border-dashed border-black/10 bg-[var(--gray-soft)]/40 opacity-70"
                }`}
              >
                <div
                  className={`relative flex h-11 w-11 items-center justify-center rounded-full text-xl transition ${
                    badge.unlocked ? "bg-white shadow-sm" : "bg-white/50"
                  }`}
                >
                  <span className={badge.unlocked ? "" : "grayscale opacity-50"}>{badge.emoji}</span>
                  {!badge.unlocked && (
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--navy)] text-white">
                      <Lock size={8} />
                    </span>
                  )}
                </div>
                <p className="text-[11px] font-semibold leading-tight text-[var(--navy)]">
                  {badge.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FERRAMENTAS */}
      <section>
        <SectionHeader eyebrow="Ferramentas" title="Acelere sua execução" />
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ToolCard
            to="/membros/diagnostico"
            icon={<Compass size={22} />}
            title="Diagnóstico"
            desc="Descubra o negócio ideal para seu perfil em menos de 2 minutos."
            cta="Abrir diagnóstico"
            tint="green"
          />
          <ToolCard
            to="/membros/calculadora-10k"
            icon={<Calculator size={22} />}
            title="Calculadora 10K"
            desc="Simule quantos clientes você precisa para faturar R$ 10 mil."
            cta="Abrir calculadora"
            tint="navy"
          />
          <ToolCard
            to="/membros/validacao"
            icon={<ClipboardCheck size={22} />}
            title="Plano de 7 dias"
            desc="Roteiro dia a dia para testar sua ideia em uma semana."
            cta="Ver checklist"
            tint="gold"
          />
          <ToolCard
            to="/membros/plano-30-dias"
            icon={<CalendarDays size={22} />}
            title="Plano de 30 dias"
            desc="Estrutura completa para lançar seu negócio no primeiro mês."
            cta="Ver plano"
            tint="green"
          />
        </div>
      </section>

      {/* FAVORITOS */}
      <section>
        <SectionHeader
          eyebrow="Favoritos"
          title="Suas ideias favoritas"
          action={favCount > 0 ? { to: "/membros/favoritos", label: "Ver todos" } : undefined}
        />
        {favLoading ? (
          <SkeletonGrid n={3} />
        ) : favCount > 0 ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(favorites ?? []).slice(0, 6).map((f: any) => (
              <Link
                key={f.business?.id}
                to="/membros/negocios/$slug"
                params={{ slug: f.business?.slug }}
                className="group rounded-2xl border border-black/5 bg-white p-5 transition hover:-translate-y-0.5 hover:border-[var(--green)]/30 hover:shadow-lg"
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
                  {f.business?.category?.name ?? "—"}
                </p>
                <p className="mt-1.5 font-display text-base font-extrabold leading-snug">
                  {f.business?.title}
                </p>
                <p className="mt-1.5 line-clamp-2 text-sm text-[var(--gray-ink)]">
                  {f.business?.summary}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] opacity-70 transition group-hover:opacity-100">
                  Abrir <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Heart size={22} />}
            title="Você ainda não favoritou nenhum negócio."
            desc="Marque como favorito as ideias que mais chamarem sua atenção enquanto explora a biblioteca."
            action={{ to: "/membros/negocios", label: "Explorar negócios" }}
          />
        )}
      </section>

      {/* PROGRESSO DETALHADO */}
      <section className="rounded-3xl border border-black/5 bg-gradient-to-br from-white via-white to-[var(--beige)]/40 p-6 lg:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1">
            <SectionTitle icon={<Target size={16} />} title="Progresso na biblioteca" />
            <p className="mt-3 text-[15px] text-[var(--gray-ink)]">
              Você explorou{" "}
              <span className="font-display text-2xl font-extrabold text-[var(--navy)]">
                {analyzedCount}
              </span>{" "}
              dos{" "}
              <span className="font-display text-2xl font-extrabold text-[var(--navy)]">
                {TOTAL_BUSINESSES}
              </span>{" "}
              negócios.
            </p>
            <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-white shadow-inner">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[var(--green)] to-[var(--gold)] transition-[width] duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <div className="rounded-2xl border border-[var(--gold)]/25 bg-white/70 p-5 backdrop-blur lg:max-w-xs">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--gold)]">
              Próximo objetivo
            </p>
            <p className="mt-1.5 text-sm text-[var(--navy)]">
              Explore mais <strong>{nextGoalGap || 3}</strong> negócios para desbloquear a próxima
              conquista.
            </p>
            <Link
              to="/membros/negocios"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--green)] hover:underline"
            >
              Continuar <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Building blocks ---------- */

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: { to: string; label: string };
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--green)]">
            {eyebrow}
          </p>
        )}
        <h2 className="mt-1 font-display text-2xl font-extrabold">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-[var(--gray-ink)]">{subtitle}</p>}
      </div>
      {action && (
        <Link
          to={action.to as any}
          className="group inline-flex items-center gap-1 text-sm font-semibold text-[var(--navy)] hover:text-[var(--green)]"
        >
          {action.label}
          <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--navy)]/[0.06] text-[var(--navy)]">
        {icon}
      </span>
      <h2 className="font-display text-xl font-extrabold">{title}</h2>
    </div>
  );
}

function MetricCard({
  icon,
  value,
  label,
  accent,
  isString,
}: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  accent: "navy" | "green" | "gold";
  isString?: boolean;
}) {
  const tone =
    accent === "green"
      ? "text-[var(--green)] bg-[var(--green)]/10"
      : accent === "gold"
        ? "text-[var(--gold)] bg-[var(--gold)]/10"
        : "text-[var(--navy)] bg-[var(--navy)]/[0.06]";
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-black/5 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-md">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tone}`}>
        {icon}
      </div>
      <p
        className={`mt-3 font-display font-extrabold text-[var(--navy)] ${
          isString ? "text-lg" : "text-3xl"
        }`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[13px] text-[var(--gray-ink)]">{label}</p>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[var(--green)]/30 to-transparent opacity-0 transition group-hover:opacity-100"
      />
    </div>
  );
}

function RecommendedCard({ recommended }: { recommended: any }) {
  if (!recommended) {
    return (
      <div className="rounded-3xl border border-black/5 bg-white p-6 lg:p-8">
        <SectionTitle icon={<Sparkles size={16} />} title="Recomendado para você" />
        <div className="mt-4 h-40 animate-pulse rounded-2xl bg-[var(--gray-soft)]/60" />
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-3xl border border-[var(--green)]/20 bg-gradient-to-br from-white via-white to-[var(--green)]/[0.06] p-6 lg:p-8">
      <div className="flex items-center justify-between">
        <SectionTitle icon={<Sparkles size={16} />} title="Recomendado para você" />
        <span className="hidden rounded-full bg-[var(--green)] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white sm:inline-block">
          Match do seu perfil
        </span>
      </div>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
        {recommended.category?.name ?? "Negócio em destaque"}
      </p>
      <h3 className="mt-1 font-display text-2xl font-extrabold leading-tight">
        {recommended.title}
      </h3>
      <p className="mt-2 line-clamp-3 text-sm text-[var(--gray-ink)]">{recommended.summary}</p>
      <dl className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <MiniStat label="Investimento" value={formatRange(recommended.investment_min_cents, recommended.investment_max_cents)} />
        <MiniStat label="Dificuldade" value={cap(recommended.difficulty)} />
        <MiniStat label="Escalabilidade" value={cap(recommended.scalability)} />
      </dl>
      <Link
        to="/membros/negocios/$slug"
        params={{ slug: recommended.slug }}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-[var(--navy)]/15 transition hover:-translate-y-0.5 hover:bg-[var(--navy)]/90"
      >
        Ver negócio <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function ContinueCard({ row }: { row: any }) {
  const b = row.business;
  const pct = row.analyzed ? 100 : 45;
  return (
    <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-white p-6 lg:p-8">
      <SectionTitle icon={<Flame size={16} />} title="Continue de onde parou" />
      <div className="mt-4 flex gap-4">
        <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--navy)] via-[var(--navy)] to-[var(--green)] text-white">
          <span className="font-display text-2xl font-extrabold">
            {b?.title?.slice(0, 1) ?? "•"}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
            {b?.category?.name ?? "—"}
          </p>
          <h3 className="mt-0.5 truncate font-display text-lg font-extrabold">{b?.title}</h3>
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--gray-soft)]">
              <div
                className="h-full rounded-full bg-[var(--green)] transition-[width] duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-[var(--gray-ink)]">{pct}%</span>
          </div>
        </div>
      </div>
      <Link
        to="/membros/negocios/$slug"
        params={{ slug: b?.slug }}
        className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[var(--navy)]/15 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--navy)] transition hover:-translate-y-0.5 hover:bg-[var(--gray-soft)]"
      >
        Continuar <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function ExploreCard({ b }: { b: any }) {
  return (
    <Link
      to="/membros/negocios/$slug"
      params={{ slug: b.slug }}
      className="group flex flex-col rounded-2xl border border-black/5 bg-white p-5 transition hover:-translate-y-0.5 hover:border-[var(--navy)]/20 hover:shadow-lg"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
        {b.category?.name ?? "—"}
      </p>
      <h3 className="mt-1.5 font-display text-lg font-extrabold leading-snug">{b.title}</h3>
      <p className="mt-2 line-clamp-2 flex-1 text-sm text-[var(--gray-ink)]">{b.summary}</p>
      <div className="mt-4 flex items-center justify-between border-t border-black/5 pt-3 text-xs text-[var(--gray-ink)]">
        <span>{formatRange(b.investment_min_cents, b.investment_max_cents)}</span>
        <span className="capitalize">{b.difficulty ?? "—"}</span>
      </div>
    </Link>
  );
}

function ToolCard({
  to,
  icon,
  title,
  desc,
  cta,
  tint,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
  tint: "navy" | "green" | "gold";
}) {
  const bg =
    tint === "green"
      ? "from-[var(--green)]/10"
      : tint === "gold"
        ? "from-[var(--gold)]/10"
        : "from-[var(--navy)]/[0.08]";
  const iconTone =
    tint === "green"
      ? "bg-[var(--green)] text-white"
      : tint === "gold"
        ? "bg-[var(--gold)] text-white"
        : "bg-[var(--navy)] text-white";
  return (
    <Link
      to={to as any}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-black/5 bg-white p-6 transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${bg} to-transparent blur-2xl transition group-hover:scale-125`}
      />
      <div
        className={`relative flex h-12 w-12 items-center justify-center rounded-2xl shadow-md ${iconTone}`}
      >
        {icon}
      </div>
      <h3 className="relative mt-4 font-display text-lg font-extrabold">{title}</h3>
      <p className="relative mt-1.5 flex-1 text-sm text-[var(--gray-ink)]">{desc}</p>
      <span className="relative mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--navy)]">
        {cta}
        <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function MiniStat({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="rounded-xl border border-black/5 bg-white/70 p-2.5 backdrop-blur">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-[13px] font-semibold text-[var(--navy)]">{value ?? "—"}</dd>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  desc,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  action: { to: string; label: string };
}) {
  return (
    <div className="mt-5 flex flex-col items-center justify-center rounded-3xl border border-dashed border-black/10 bg-white p-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--green)]/10 text-[var(--green)]">
        {icon}
      </div>
      <p className="mt-4 font-display text-lg font-extrabold text-[var(--navy)]">{title}</p>
      <p className="mt-1 max-w-md text-sm text-[var(--gray-ink)]">{desc}</p>
      <Link
        to={action.to as any}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-4 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[var(--navy)]/90"
      >
        {action.label} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function SkeletonGrid({ n }: { n: number }) {
  return (
    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="h-40 animate-pulse rounded-2xl border border-black/5 bg-[var(--gray-soft)]/60"
        />
      ))}
    </div>
  );
}

function HeroArtwork({ analyzed, favorites }: { analyzed: number; favorites: number }) {
  return (
    <div className="relative h-64 w-full max-w-sm">
      {/* Floating cards */}
      <div className="absolute left-2 top-4 w-48 rotate-[-4deg] rounded-2xl border border-black/5 bg-white p-4 shadow-xl transition hover:rotate-[-2deg]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--green)]">
          Alimentação
        </p>
        <p className="mt-1 font-display text-sm font-extrabold">Hamburgueria de bairro</p>
        <div className="mt-2 flex items-center gap-1 text-[10px] text-[var(--gray-ink)]">
          <BarChart3 size={10} /> Score 78
        </div>
      </div>
      <div className="absolute right-0 top-16 w-52 rotate-[3deg] rounded-2xl border border-black/5 bg-white p-4 shadow-xl transition hover:rotate-[1deg]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--green)]">
          Digital
        </p>
        <p className="mt-1 font-display text-sm font-extrabold">Agência de IA</p>
        <div className="mt-2 flex items-center gap-1 text-[10px] text-[var(--gray-ink)]">
          <Sparkles size={10} /> Recorrente
        </div>
      </div>
      <div className="absolute bottom-2 left-8 w-56 rotate-[-2deg] rounded-2xl border border-[var(--green)]/25 bg-gradient-to-br from-white to-[var(--green)]/[0.08] p-4 shadow-xl">
        <div className="flex items-center justify-between text-[10px] font-semibold text-[var(--gray-ink)]">
          <span>Sua jornada</span>
          <span className="text-[var(--green)]">{analyzed}/{TOTAL_BUSINESSES}</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
          <div
            className="h-full rounded-full bg-[var(--green)] transition-[width]"
            style={{ width: `${Math.min(100, (analyzed / TOTAL_BUSINESSES) * 100)}%` }}
          />
        </div>
        <div className="mt-3 flex items-center gap-3 text-[10px] text-[var(--gray-ink)]">
          <span className="inline-flex items-center gap-1">
            <Heart size={10} className="text-[var(--green)]" /> {favorites} favoritos
          </span>
          <span className="inline-flex items-center gap-1">
            <UserSearch size={10} /> perfil ativo
          </span>
        </div>
      </div>
    </div>
  );
}

function cap(s: string | null) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatRelativeShort(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return "agora";
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d atrás`;
  const w = Math.floor(d / 7);
  return `${w}sem atrás`;
}

function MyProjectBlock({ activeProject }: { activeProject: any }) {
  if (!activeProject) {
    return (
      <section className="relative overflow-hidden rounded-3xl border border-[var(--green)]/20 bg-gradient-to-br from-[var(--green)]/[0.06] via-white to-white p-6 lg:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--green)]/20 bg-white px-3 py-1 text-xs font-semibold text-[var(--green)]">
              <Rocket size={12} /> Sistema de Implantação
            </span>
            <h2 className="mt-3 font-display text-2xl font-extrabold text-[var(--navy)]">
              Escolha seu primeiro negócio
            </h2>
            <p className="mt-1 max-w-xl text-sm text-[var(--gray-ink)]">
              Selecione um negócio da biblioteca como seu projeto principal. A plataforma
              organiza etapas, missões e tarefas até sua primeira venda.
            </p>
          </div>
          <Link
            to="/membros/negocios"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white hover:-translate-y-0.5 hover:bg-[var(--navy)]/90"
          >
            Escolher meu negócio <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  const p = activeProject.project;
  const b = p.business;
  const stages = activeProject.stages ?? [];
  const missions = activeProject.missions ?? [];
  const tasks = activeProject.tasks ?? [];
  const missionProgress = activeProject.missionProgress ?? [];
  const doneTaskIds = new Set(
    (activeProject.taskProgress ?? [])
      .filter((t: any) => t.completed_at)
      .map((t: any) => t.task_id),
  );
  const missionProgressMap = new Map(missionProgress.map((m: any) => [m.mission_id, m]));

  // Find next actionable mission + task
  let nextMission: any = null;
  let nextTask: any = null;
  for (const s of stages) {
    const sMissions = missions.filter((m: any) => m.stage_id === s.id);
    for (const m of sMissions) {
      const dep = m.depends_on_mission_id
        ? (missionProgressMap.get(m.depends_on_mission_id) as any)
        : null;
      if (m.depends_on_mission_id && !dep?.completed_at) continue;
      const progress = missionProgressMap.get(m.id) as any;
      if (progress?.completed_at) continue;
      if (!hasMissionWorkspaceContent(m)) continue;
      const mTasks = tasks.filter((t: any) => t.mission_id === m.id);
      const t = mTasks.find((tk: any) => !doneTaskIds.has(tk.id));
      nextMission = m;
      nextTask = t ?? null;
      break;
    }
    if (nextMission) break;
  }

  return (
    <section className="relative overflow-hidden rounded-3xl border border-black/5 bg-white p-6 shadow-sm lg:p-8">
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--green)]/20 bg-[var(--green)]/[0.06] px-3 py-1 text-xs font-semibold text-[var(--green)]">
            <Rocket size={12} /> Meu Projeto
          </span>
          <h2 className="mt-3 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
            {b?.title}
          </h2>
          <p className="mt-1 text-sm text-[var(--gray-ink)]">{b?.category?.name}</p>

          <div className="mt-5 max-w-xl">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold text-[var(--gray-ink)]">
              <span>Progresso do projeto</span>
              <span className="text-[var(--navy)]">
                {Number(p.progress_pct).toFixed(0)}%
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--gray-soft)]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[var(--green)] to-[var(--green)]/70 transition-[width] duration-700"
                style={{ width: `${Math.min(100, Number(p.progress_pct))}%` }}
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/membros/meu-projeto"
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-4 py-2.5 text-sm font-semibold text-white hover:-translate-y-0.5 hover:bg-[var(--navy)]/90"
            >
              Continuar jornada <ArrowRight size={14} />
            </Link>
            {b?.slug && (
              <Link
                to="/membros/negocios/$slug"
                params={{ slug: b.slug }}
                className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
              >
                Ver ficha
              </Link>
            )}
          </div>
        </div>

        {nextTask && (
          <div className="rounded-2xl border border-[var(--green)]/20 bg-[var(--green)]/[0.04] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
              Próxima tarefa
            </p>
            <p className="mt-1 font-display text-base font-extrabold text-[var(--navy)]">
              {nextTask.title}
            </p>
            <p className="mt-0.5 text-xs text-[var(--gray-ink)]">
              Missão: {nextMission?.title}
            </p>
            <Link
              to="/membros/meu-projeto/missao/$id"
              params={{ id: nextMission.id }}
              search={{}}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[var(--green)] px-3 py-2 text-xs font-semibold text-white hover:bg-[var(--green)]/90"
            >
              Executar <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function WeeklySummaryBlock({ weekly }: { weekly: any }) {
  const tasks = weekly.tasksThisWeek ?? 0;
  const missions = weekly.missionsThisWeek ?? 0;
  const days = weekly.daysSinceLastActivity;
  const recovery = weekly.needsRecovery;
  const stalled = days !== null && days >= 3 && !recovery;

  return (
    <section
      className={`rounded-3xl border p-6 lg:p-8 ${
        recovery
          ? "border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/[0.08] to-white"
          : "border-black/5 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Resumo da semana
          </p>
          <h2 className="mt-1 font-display text-xl font-extrabold text-[var(--navy)] lg:text-2xl">
            {recovery
              ? "Vamos retomar sua jornada?"
              : tasks > 0
                ? `Você concluiu ${tasks} tarefa${tasks === 1 ? "" : "s"} esta semana`
                : "Nenhuma tarefa nesta semana ainda"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--gray-ink)]">
            {recovery
              ? `Faz ${days} dias sem atividade em ${weekly.businessTitle ?? "seu projeto"}. Uma tarefa pequena hoje mantém o ritmo — comece pela próxima ação sugerida.`
              : stalled
                ? `Última atividade há ${days} dia${days === 1 ? "" : "s"}. Que tal marcar mais uma tarefa hoje?`
                : `${missions} missão${missions === 1 ? "" : "ões"} concluída${missions === 1 ? "" : "s"} nos últimos 7 dias. Progresso atual: ${weekly.progressPct}%.`}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <WeekMiniStat label="Tarefas" value={tasks} accent="green" />
          <WeekMiniStat label="Missões" value={missions} accent="navy" />
          <WeekMiniStat
            label={days === null ? "Sem dados" : days === 0 ? "Ativo hoje" : `${days}d atrás`}
            value={days === null ? "—" : days === 0 ? "✓" : String(days)}
            accent="gold"
          />

        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to="/membros/meu-projeto"
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--navy)]/90"
        >
          {recovery ? "Retomar agora" : "Abrir meu projeto"} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

function WeekMiniStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: "green" | "navy" | "gold";
}) {
  const color =
    accent === "green"
      ? "text-[var(--green)]"
      : accent === "gold"
        ? "text-[var(--gold)]"
        : "text-[var(--navy)]";
  return (
    <div className="min-w-[76px] rounded-xl border border-black/5 bg-white px-3 py-2 text-center">
      <p className={`font-display text-xl font-extrabold ${color}`}>{value}</p>
      <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </p>
    </div>
  );
}
